/**
 * Transport — the wire under the SDK. The client is written against this
 * interface so the SAME typed surface runs over PostgREST in production and over
 * PGlite in tests. It exposes exactly what the governed boundary needs — RPCs
 * (scalar + table) and single-table CRUD — and nothing that would let a host
 * reach across schemas or bypass RLS.
 */
/**
 * What the transport KNEW, instead of a sentence about it.
 *
 * This used to be a plain `Error` whose message interpolated the status, and
 * every consumer that needed the number recovered it with a three-digit regex
 * over that message — `refusal.ts`, the console's `classifyError`, the portal's
 * own inline copy. Three regexes reconstructing a number the throw site was
 * holding.
 *
 * The cost was not the parsing. It was that a regex cannot express what the
 * status MEANS, so nothing distinguished 500 from 503 — and a deployment whose
 * PostgREST had been stopped for ten days told its operator "the server
 * returned an error (503). Try again" (SLM-MOONBASE #124).
 *
 * **The message string is unchanged, deliberately.** Existing readers keep
 * working while they migrate to the fields; a formatting change here would have
 * been a silent behaviour change in three places at once.
 */
export class SolumTransportError extends Error {
  readonly status: number
  readonly statusText: string
  /**
   * The response body, verbatim — a governed door's refusal envelope lives here.
   *
   * Readable, and deliberately NOT ENUMERABLE. This is server text of unbounded
   * shape: a PostgREST error carries the failing statement, and a refusal
   * envelope carries the subject and the row it was refused on. Structured
   * logging, `JSON.stringify(error)` and every "attach the error object" path
   * copy own ENUMERABLE properties, so leaving it enumerable is how a customer's
   * row ends up pasted into an incident ticket by a handler that only meant to
   * record that something failed. `status`, `statusText` and `origin` stay
   * enumerable because they are bounded and are what a log actually wants.
   *
   * `declare` matters: this package targets ES2022, so `useDefineForClassFields`
   * is on and a plain field declaration would emit an enumerable `payload;`
   * that overwrites the descriptor below.
   */
  declare readonly payload: string
  /** Request ORIGIN only. Never the path, never a query, never a credential. */
  readonly origin: string | null

  constructor(status: number, statusText: string, payload: string, url?: string) {
    super(`solum sdk: ${status} ${statusText} — ${payload}`)
    this.name = 'SolumTransportError'
    this.status = status
    this.statusText = statusText
    this.origin = originOf(url)
    Object.defineProperty(this, 'payload', {
      value: payload, enumerable: false, writable: false, configurable: true,
    })
  }
}

/** An origin, or nothing. A malformed URL must not become part of an error message. */
function originOf(url: string | undefined): string | null {
  if (!url) return null
  try { return new URL(url).origin } catch { return null }
}

export type Row = Record<string, unknown>
export interface SelectOptions {
  columns?: string
  where?: Row       // equality filters, ANDed
  limit?: number
  offset?: number
  /** PostgREST-native filters, e.g. { key: 'status', value: 'in.(paid,void)' } (#113). */
  filters?: { key: string; value: string }[]
  /** PostgREST order clause, e.g. 'issue_date.desc.nullslast'. */
  order?: string
}

/**
 * What a write hands BACK (#2444 slice 4).
 *
 * `representation` (the default) returns the whole written row, which PostgREST
 * reads back with the caller's SELECT privilege. A `subject` entity has had
 * that privilege revoked — its rows are disclosed only by the governed doors,
 * which record each disclosure — so a full read-back there is refused
 * (`permission denied`). `id` asks for the row handle alone (`select=id`, the
 * one column an API role keeps on a subject table with a direct write door);
 * the caller then reads the row THROUGH the door.
 */
export interface WriteOptions {
  returning?: 'representation' | 'id'
}

/** A page of rows plus the TOTAL the filter matches (null when the server did not report one). */
export interface Page<T> {
  rows: T[]
  /** Total matching rows server-side, from PostgREST's Content-Range. */
  total: number | null
}

export interface Transport {
  /** Call a Postgres function returning a single scalar/JSON value. */
  rpcScalar<T = unknown>(fn: string, args?: Row, schema?: string): Promise<T>
  /** Call a set-returning Postgres function. */
  rpcTable<T = Row>(fn: string, args?: Row, schema?: string): Promise<T[]>
  /** Read rows from one schema.table (RLS applies). */
  select<T = Row>(schema: string, table: string, opts?: SelectOptions): Promise<T[]>
  /**
   * Read a page AND the total that matches. `select()` alone cannot tell a caller
   * whether it saw everything, which is how the console ended up silently
   * truncating every list at 500 rows with no indication (#110).
   */
  selectPage<T = Row>(schema: string, table: string, opts?: SelectOptions): Promise<Page<T>>
  /** Insert one row (RLS `with check` applies). */
  insert<T = Row>(schema: string, table: string, row: Row, opts?: WriteOptions): Promise<T>
  /** Patch a row by id (RLS `using` + `with check` apply). */
  update<T = Row>(schema: string, table: string, id: string, patch: Row, opts?: WriteOptions): Promise<T>
  /**
   * Delete by KEY (RLS `using` applies). A string is the `id` shorthand; a Row
   * is the key's columns, ANDed exactly the way `SelectOptions.where` is.
   *
   * The Row form is not a convenience. Not every governed table HAS an `id`:
   * `core.user_view_pref` is keyed `(user_id, entity_type)` with no id column at
   * all (core `0030_personas_experience.sql:117-122`), so the shorthand put
   * `id=eq.…` on the wire and PostgREST answered 42703 *column
   * user_view_pref.id does not exist* — on every un-pin and every CHANGE of
   * pin, on every live deployment, for as long as the console's view pin has
   * shipped. A transport that can only address one key shape does not make the
   * caller careful, it makes the mistake invisible.
   */
  delete(schema: string, table: string, key: string | Row): Promise<void>
  /**
   * A sibling transport that sends `extra` on EVERY request, the same identity
   * and endpoint otherwise (#2444). OPTIONAL on purpose: it is how one tool call
   * stamps its correlation id on everything it does, and a test double that
   * does not implement it simply produces uncorrelated requests rather than a
   * compile error in every fake in the tree.
   */
  withHeaders?(extra: Record<string, string>): Transport
}

// ── Production transport: PostgREST over HTTP ────────────────────────────────

/**
 * Where the bearer token comes from.
 *
 * A plain string is the right thing for a server-side caller holding a service
 * token for the life of the process. A BROWSER cannot use one: a real GoTrue
 * access token expires (an hour by default) and is replaced by a refresh, so a
 * token captured at construction is stale within the session. Passing a
 * function lets the caller hand over the CURRENT token per request — which is
 * what makes a real sign-in possible at all (SEC-03).
 */
export type TokenSource = string | (() => string | Promise<string>)

export interface PostgrestConfig {
  url: string          // the Supabase URL (deployment-agnostic — from config)
  token: TokenSource   // a user JWT (RLS applies), a service token, or a getter for either
  anonKey?: string     // the anon apikey, if distinct from the token
  restPath?: string    // path prefix to PostgREST (default '/rest/v1', the Supabase/Kong route;
                       // set '' to target a PostgREST instance directly)
  /**
   * Told what happened on the WIRE, for every request this transport makes
   * (#1790).
   *
   * The first design observed read failures at the TanStack cache boundary
   * instead, and an adversarial review showed why that cannot work: a `queryFn`
   * written `try { … } catch { return null }` — and this repo has six —
   * swallows a 503 and resolves, so the cache reports success and a degraded
   * banner clears itself while the service is still down. The cache sees what
   * the application decided; only the transport sees what the server did.
   *
   * It is also the only observation point that covers WRITES.
   *
   * ⚠️ It DOES report an abort. An earlier version of this comment claimed
   * cancellations are invisible "because a cancelled request never produces a
   * response" — but `send` catches every rejection, and an `AbortError` is a
   * rejection, so it would be reported as `{ok: false, status: null}`. Nothing
   * threads an `AbortSignal` through this transport today, so the only live
   * exposure is in-flight requests at page unload. Anyone adding `signal` must
   * filter `AbortError` here first.
   *
   * Deliberately narrow: `ok` and a status. No path, no query, no body, no
   * headers — nothing a caller could log into an incident ticket by accident.
   */
  onOutcome?: (outcome: { ok: boolean; status: number | null }) => void
  /**
   * Headers sent on every request, set by `withHeaders` (#2444) — e.g. one
   * tool call's `x-solum-correlation-id`. They can never replace the identity
   * or profile headers: those are applied after these.
   */
  extraHeaders?: Record<string, string>
  /**
   * #2444 — a SERVER-SIDE caller that fronts PostgREST directly (the relay's
   * copilot and internal-MCP hops, which never pass Kong and so are in no Kong
   * http-log) is told of EVERY exchange this transport makes: once per
   * completed response — 2xx, 4xx or 5xx, with or without the database-signed
   * `x-solum-receipt-context` — and once per request that got no response at
   * all. See `RequestObservation` for exactly what travels.
   *
   * It used to fire only when the context header was present, and that made
   * the refusals invisible: a request PostgREST refuses before
   * `core.pre_request` runs (a bad or expired JWT, a 401/403 at the door)
   * carries no context, so the one outcome an auditor most needs was the one
   * the relay never reported. A context is ATTRIBUTION, not admission to the
   * record — its absence means "unattributed", never "unobserved".
   *
   * A browser never sees the header (Kong strips it from every client
   * response) and never needs this: its requests pass Kong, which captures
   * them itself. Wiring it on a transport that DOES pass Kong would record
   * each request twice.
   */
  onRequestObserved?: (observation: RequestObservation) => void
  /**
   * #2444 revised slice 2 — the relay's INLINE RECEIPT GATE for a direct
   * PostgREST hop (the copilot's `rest` and the internal MCP's `rest-mcp`).
   *
   * Unlike `onRequestObserved` it is AWAITED: the Response is not handed to the
   * caller until the gate resolves, so a governed answer never reaches the
   * relay's own code before its receipt has committed.
   *
   *   · resolves   → the upstream Response is returned untouched;
   *   · rejects    → the upstream body is CANCELLED and the caller gets a
   *                  synthetic `503` with the fixed `GATE_REFUSED_BODY` — no
   *                  upstream status, header or byte;
   *   · no response at all (`transport_error`) → the gate is told (it counts
   *                  `delivery_failed`, never a receipt) and the original
   *                  error is re-thrown unchanged.
   *
   * The gate never re-routes: it only withholds. Which PostgREST a transport
   * talks to is still `url` alone.
   */
  gate?: (observation: RequestObservation) => Promise<void>
}

/** The status and body a caller receives when the receipt gate refuses to
 *  release an answer (#2444). FIXED: nothing of the upstream response travels. */
export const GATE_REFUSED_STATUS = 503
export const GATE_REFUSED_BODY = '{"code":"SOLUM_RECEIPT_UNAVAILABLE","message":"The request could not be recorded."}'

/** A fresh synthetic refusal — a Response body can be read once, so never shared. */
export function gateRefusedResponse(): Response {
  return new Response(GATE_REFUSED_BODY, {
    status: GATE_REFUSED_STATUS, statusText: 'Service Unavailable',
    headers: { 'content-type': 'application/json', 'cache-control': 'no-store' },
  })
}

/** What an exchange may be told to: a fire-and-forget observer, an awaited gate, or both. */
export interface ExchangeHooks {
  observe?: (observation: RequestObservation) => void
  gate?: (observation: RequestObservation) => Promise<void>
}

/**
 * What a direct PostgREST caller observed about ONE HTTP exchange (#2444).
 *
 * Deliberately narrow. `context` is the opaque, database-signed header or
 * null; `path` is the request's pathname RELATIVE to the transport's base and
 * only when it names one table or one function (`GOVERNED_PATH`), else null.
 * Never a query string, never a body, never a credential, never any other
 * header — nothing a capture line could leak into a log.
 *
 * `transport_error` is a request that got NO response (DNS, refused
 * connection, reset, abort). It carries no status because there was none, and
 * it must never be turned into a receipt: nothing answered, so there is no
 * outcome to attribute.
 */
export type RequestObservation =
  | { kind: 'response'; context: string | null; method: string; path: string | null; status: number;
      contentRange: string | null; durationMs: number
      /** GATED exchanges only (#2444 H6): the `x-solum-correlation-id` this
       *  request carried — the gate binds the database's write attestation to
       *  it. A join key, never an authority. */
      correlationId?: string
      /** GATED exchanges only, and only when the request carried one: its
       *  `Prefer` value(s) (duplicates joined by ", " as Headers does). The gate
       *  never releases an attested write whose Prefer ends the transaction
       *  otherwise than by commit (#2444 fix2). */
      prefer?: string }
  | { kind: 'transport_error'; method: string; path: string | null; durationMs: number }

/** The only path shape an observation may carry: one table, or one function,
 *  directly under the base. The writer (`core.capture_request_receipt`) reads
 *  exactly these two shapes; anything else is reported as null, not guessed. */
const GOVERNED_PATH = /^\/(rpc\/)?[A-Za-z0-9_]{1,63}$/

const CORRELATION_HEADER = 'x-solum-correlation-id'
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/**
 * A GATED exchange (#2444 H6) always carries exactly one correlation id the
 * database will adopt as-is (a lower-case canonical uuid): the caller's own
 * when it is one, else a fresh one. The gate needs to KNOW it — the database's
 * write attestation binds it — so it is never left for the database to mint.
 */
function withGateCorrelation(init: RequestInit | undefined): { init: RequestInit; correlationId: string } {
  const headers = new Headers(init?.headers)
  const own = (headers.get(CORRELATION_HEADER) ?? '').toLowerCase()
  const correlationId = UUID_RE.test(own) ? own : globalThis.crypto.randomUUID()
  headers.set(CORRELATION_HEADER, correlationId)
  return { init: { ...(init ?? {}), headers }, correlationId }
}

/** Parsing base for a RELATIVE url (a browser host may configure one). Never
 *  reported: only a pathname under the same origin survives. */
const PARSE_BASE = 'http://solum.invalid'

/**
 * `input`'s pathname relative to `base`'s — when it is a governed path —
 * else null. The query string is never read: `URL.pathname` excludes it, so a
 * filter value (`?email=eq.…`) cannot reach an observation by construction.
 */
export function governedPath(base: string, input: string): string | null {
  let basePath: string
  let reqPath: string
  try {
    const b = new URL(base, PARSE_BASE)
    const u = new URL(input, PARSE_BASE)
    if (b.origin !== u.origin) return null
    basePath = b.pathname.replace(/\/+$/, '')
    reqPath = u.pathname
  } catch { return null }
  if (basePath && !reqPath.startsWith(`${basePath}/`)) return null
  const rel = reqPath.slice(basePath.length)
  return GOVERNED_PATH.test(rel) ? rel : null
}

/** An observer must never break, delay or alter the exchange it observes —
 *  neither by throwing nor by returning a promise that later rejects (which
 *  Node would otherwise raise as an unhandled rejection and exit on). */
function notify(observer: (o: RequestObservation) => void, o: RequestObservation): void {
  try {
    const r: unknown = observer(o)
    if (r && typeof (r as PromiseLike<unknown>).then === 'function') {
      (r as PromiseLike<unknown>).then(undefined, () => undefined)
    }
  } catch { /* swallowed on purpose: see above */ }
}

/**
 * `fetch`, OBSERVED — the one definition of what a direct PostgREST caller
 * reports (#2444). The transport's own `send` is built on it, and so is any
 * server-side caller that must make a raw PostgREST call (the copilot's
 * invocation recorder), so the path rule, the method and the error shape
 * cannot drift between the two.
 *
 * One observation per call of this function, which is one HTTP exchange:
 * there is no retry in here. A caller that retries calls it again and is
 * observed again — each attempt is its own exchange with its own outcome.
 * A rejection is reported as `transport_error` and re-thrown UNCHANGED.
 *
 * With a `gate` (#2444 revised slice 2) the same observation is AWAITED before
 * the Response is returned; see `PostgrestConfig.gate` for the three outcomes.
 * A plain function is the observer alone (the original signature).
 */
export function observedFetch(base: string, hooks?: ((o: RequestObservation) => void) | ExchangeHooks):
  (input: string, init?: RequestInit) => Promise<Response> {
  const observer = typeof hooks === 'function' ? hooks : hooks?.observe
  const gate = typeof hooks === 'function' ? undefined : hooks?.gate
  return async (input, requested) => {
    if (!observer && !gate) return fetch(input, requested)
    const scoped = gate ? withGateCorrelation(requested) : null
    const init = scoped ? scoped.init : requested
    const method = (init?.method ?? 'GET').toUpperCase()
    const path = governedPath(base, input)
    const t0 = Date.now()
    let res: Response
    try {
      res = await fetch(input, init)
    } catch (error) {
      const o: RequestObservation = { kind: 'transport_error', method, path, durationMs: Date.now() - t0 }
      if (observer) notify(observer, o)
      // Told, then the ORIGINAL error: a gate rejection here has no response to
      // replace, so it never changes what the caller sees.
      if (gate) { try { await gate(o) } catch { /* see above */ } }
      throw error
    }
    const header = (name: string): string | null => {
      const h = res.headers as Headers | undefined
      return (typeof h?.get === 'function' ? h.get(name) : null) || null
    }
    const o: RequestObservation = { kind: 'response', context: header('x-solum-receipt-context'), method, path,
      status: res.status, contentRange: header('content-range'), durationMs: Date.now() - t0,
      ...(scoped ? { correlationId: scoped.correlationId } : {}),
      ...(scoped && new Headers(init?.headers).get('prefer') !== null
        ? { prefer: new Headers(init?.headers).get('prefer')! } : {}) }
    if (observer) notify(observer, o)
    if (gate) {
      try {
        await gate(o)
      } catch {
        // WITHHELD: the upstream body is cancelled unread and nothing of the
        // upstream response — status, header or byte — reaches the caller.
        try { await res.body?.cancel() } catch { /* already closed */ }
        return gateRefusedResponse()
      }
    }
    return res
  }
}

/**
 * A thin PostgREST client. Multi-schema via `Accept-Profile` / `Content-Profile`
 * so a host reaches a module's schema without a cross-schema join. RLS is enforced
 * server-side against the `token`'s identity — the SDK never sees more than the
 * caller may.
 */
export function postgrestTransport(cfg: PostgrestConfig): Transport {
  const base = cfg.url.replace(/\/$/, '') + (cfg.restPath ?? '/rest/v1')
  // Resolved PER REQUEST, not captured once. With a getter this is what lets a
  // browser session refresh underneath a long-lived client without rebuilding
  // it — and it means an expired token is never sent twice.
  const headers = async (extra: Record<string, string> = {}): Promise<Record<string, string>> => {
    const token = typeof cfg.token === 'function' ? await cfg.token() : cfg.token
    return {
      // FIRST, so a scoped header can never stand in for the bearer, the
      // apikey or a schema profile — those are written over it below.
      ...(cfg.extraHeaders ?? {}),
      apikey: cfg.anonKey ?? token,
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
      ...extra,
    }
  }
  /**
   * `fetch` that reports a REJECTION as an outcome too.
   *
   * `body()` only ever sees a `Response`, so a request that never got one — DNS
   * gone, TLS refused, the laptop's wifi dropped — would otherwise be invisible
   * to the observer, which is exactly the case a person most needs named. The
   * rejection is re-thrown untouched; this only watches.
   *
   * EVERY request of this transport goes through here exactly once — the RPCs,
   * `select`, `selectPage` (the count read), and the three writes — and there
   * is no retry, no HEAD and no auth refresh on this wire (a token getter's
   * own refresh is its caller's exchange with GoTrue, not PostgREST's). So
   * `onRequestObserved` sees one observation per HTTP exchange.
   */
  const observed = observedFetch(base, { observe: cfg.onRequestObserved, gate: cfg.gate })
  const send = async (input: string, init?: RequestInit): Promise<Response> => {
    try {
      return await observed(input, init)
    } catch (error) {
      cfg.onOutcome?.({ ok: false, status: null })
      throw error
    }
  }

  const body = async (res: Response): Promise<unknown> => {
    /* Reported BEFORE the throw, so a failure is observed even though the caller
       may swallow the exception a moment later. */
    cfg.onOutcome?.({ ok: res.ok, status: res.status })
    if (!res.ok) throw new SolumTransportError(res.status, res.statusText, await res.text(), cfg.url)
    const text = await res.text()
    return text ? JSON.parse(text) : null
  }
  const query = (opts?: SelectOptions): string => {
    const p = new URLSearchParams()
    p.set('select', opts?.columns ?? '*')
    if (opts?.limit != null) p.set('limit', String(opts.limit))
    if (opts?.offset != null) p.set('offset', String(opts.offset))
    if (opts?.order) p.set('order', opts.order)
    for (const [k, v] of Object.entries(opts?.where ?? {})) p.set(k, `eq.${String(v)}`)
    // Filtering and ORDERING belong on the server. Doing them in the browser over
    // a fetched page produces answers that are wrong rather than merely partial —
    // a status filter reported 125 matches against a true 50,022 (#113).
    for (const f of opts?.filters ?? []) p.append(f.key, f.value)
    return p.toString()
  }
  const rpc = async (fn: string, args: Row, schema: string): Promise<unknown> =>
    body(await send(`${base}/rpc/${fn}`, {
      method: 'POST', headers: await headers({ 'content-profile': schema }), body: JSON.stringify(args),
    }))
  return {
    withHeaders(extra: Record<string, string>) {
      return postgrestTransport({ ...cfg, extraHeaders: { ...(cfg.extraHeaders ?? {}), ...extra } })
    },
    async rpcScalar<T>(fn: string, args: Row = {}, schema = 'core') { return (await rpc(fn, args, schema)) as T },
    async rpcTable<T>(fn: string, args: Row = {}, schema = 'core') { return (await rpc(fn, args, schema)) as T[] },
    async select<T>(schema: string, table: string, opts?: SelectOptions) {
      return (await body(await send(`${base}/${table}?${query(opts)}`, { headers: await headers({ 'accept-profile': schema }) }))) as T[]
    },
    async selectPage<T>(schema: string, table: string, opts?: SelectOptions) {
      // `Prefer: count=exact` makes PostgREST report the full match count in
      // Content-Range as `start-end/total`, so a truncated list can say so.
      const res = await send(`${base}/${table}?${query(opts)}`,
        { headers: await headers({ 'accept-profile': schema, prefer: 'count=exact' }) })
      const rows = (await body(res)) as T[]
      const range = res.headers.get('content-range') ?? ''
      const total = Number(range.split('/')[1])
      return { rows, total: Number.isFinite(total) ? total : null }
    },
    async insert<T>(schema: string, table: string, row: Row, opts?: WriteOptions) {
      const rows = await body(await send(`${base}/${table}${opts?.returning === 'id' ? '?select=id' : ''}`, {
        method: 'POST', headers: await headers({ 'content-profile': schema, prefer: 'return=representation' }), body: JSON.stringify(row),
      }))
      return (Array.isArray(rows) ? rows[0] : rows) as T
    },
    async update<T>(schema: string, table: string, id: string, patch: Row, opts?: WriteOptions) {
      const rows = await body(await send(`${base}/${table}?id=eq.${encodeURIComponent(id)}${opts?.returning === 'id' ? '&select=id' : ''}`, {
        method: 'PATCH', headers: await headers({ 'content-profile': schema, prefer: 'return=representation' }), body: JSON.stringify(patch),
      }))
      return (Array.isArray(rows) ? rows[0] : rows) as T
    },
    async delete(schema: string, table: string, key: string | Row) {
      // One equality encoder for reads and deletes: `URLSearchParams` is what
      // `query()` already uses for `where`, so a key column travels the same way
      // whether it is being selected on or deleted on.
      const p = new URLSearchParams()
      for (const [k, v] of Object.entries(typeof key === 'string' ? { id: key } : key)) p.set(k, `eq.${String(v)}`)
      // An unfiltered DELETE is a table wipe PostgREST will happily send, with
      // only RLS standing between it and every row the caller may reach. An
      // empty key is a caller bug and must not reach the wire.
      if (p.toString() === '') throw new Error(`solum sdk: delete on ${schema}.${table} needs a key`)
      await body(await send(`${base}/${table}?${p.toString()}`, {
        method: 'DELETE', headers: await headers({ 'content-profile': schema }),
      }))
    },
  }
}
